/* SteelHome · Kit PDP — vanilla JS, sin dependencias */
(() => {
  if (window.__shkLoaded) return;
  window.__shkLoaded = true;
  document.documentElement.classList.add('shk-js');

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inEditor = () => Boolean(window.Shopify && window.Shopify.designMode);

  /* ---------- Scroll reveal ---------- */
  let revealIO = null;
  const initReveal = (root = document) => {
    const els = root.querySelectorAll('.shk-reveal:not(.is-in)');
    if (!els.length) return;
    if (reduced() || inEditor() || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    if (!revealIO) {
      revealIO = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.15 });
    }
    els.forEach((el) => revealIO.observe(el));
  };
  initReveal();
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

  /* ---------- Galería ---------- */
  class ShkGallery extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      const on = (el, ev, fn, opts = {}) => el && el.addEventListener(ev, fn, { signal: this.ac.signal, ...opts });
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.count = this.querySelector('[data-count]');
      this.progress = this.querySelector('[data-progress]');
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.dialog = this.querySelector('[data-zoom]');
      this.index = 0;
      if (!this.track || !this.slides.length) return;

      let frame = 0;
      on(this.track, 'scroll', () => {
        if (frame) return;
        frame = requestAnimationFrame(() => { frame = 0; this.sync(); });
      }, { passive: true });
      on(this.querySelector('[data-prev]'), 'click', () => this.go(this.index - 1));
      on(this.querySelector('[data-next]'), 'click', () => this.go(this.index + 1));
      this.thumbs.forEach((t, i) => on(t, 'click', () => this.go(i)));
      on(this.track, 'keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index + (e.key === 'ArrowRight' ? 1 : -1)); }
      });

      // Zoom: toque sin arrastre
      let startX = 0, startY = 0;
      on(this.track, 'pointerdown', (e) => { startX = e.clientX; startY = e.clientY; }, { passive: true });
      this.slides.forEach((s) => on(s, 'click', (e) => {
        if (Math.abs(e.clientX - startX) > 8 || Math.abs(e.clientY - startY) > 8) return;
        this.openZoom(s);
      }));
      this.slides.forEach((s) => on(s, 'keydown', (e) => { if (e.key === 'Enter') this.openZoom(s); }));
      if (this.dialog) {
        on(this.dialog.querySelector('[data-zoom-close]'), 'click', () => this.dialog.close());
        on(this.dialog, 'click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      }
      this.sync();
    }
    disconnectedCallback() { this.ac && this.ac.abort(); }
    padL() { return parseFloat(getComputedStyle(this.track).paddingLeft) || 0; }
    sync() {
      const x = this.track.scrollLeft + this.padL();
      let best = 0, dist = Infinity;
      this.slides.forEach((s, i) => { const d = Math.abs(s.offsetLeft - x); if (d < dist) { dist = d; best = i; } });
      if (this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2) best = this.slides.length - 1;
      this.index = best;
      const n = this.slides.length;
      if (this.count) this.count.textContent = `${best + 1} / ${n}`;
      if (this.progress) this.progress.style.setProperty('--p', ((best + 1) / n).toFixed(3));
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === best)));
    }
    go(i) {
      const n = this.slides.length;
      const target = this.slides[(i + n) % n];
      this.track.scrollTo({ left: target.offsetLeft - this.padL(), behavior: reduced() ? 'auto' : 'smooth' });
    }
    goToMedia(id) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i >= 0) this.go(i);
    }
    openZoom(slide) {
      if (!this.dialog || !slide.dataset.zoomSrc) return;
      const img = this.dialog.querySelector('img');
      img.src = slide.dataset.zoomSrc;
      img.alt = slide.dataset.zoomAlt || '';
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    }
  }
  if (!customElements.get('shk-gallery')) customElements.define('shk-gallery', ShkGallery);

  /* ---------- Compra (Releasit) + sticky ---------- */
  class ShkBuy extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      const on = (el, ev, fn) => el && el.addEventListener(ev, fn, { signal: this.ac.signal });
      const variantsEl = this.querySelector('[data-variants]');
      this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
      this.idInput = this.querySelector('[data-variant-id]');
      this.variant = this.variants.find((v) => String(v.id) === String(this.idInput && this.idInput.value));
      this.main = this.querySelector('[data-main-buy]');
      this.sticky = this.querySelector('[data-sticky]');
      this.form = this.querySelector('form');
      this.buttons = [...this.querySelectorAll('[data-buy]')];
      this.buttons.forEach((b) => { b.dataset.label = b.textContent.trim(); });
      this.native = null;

      this.buttons.forEach((b) => on(b, 'click', () => this.buy()));
      on(document, 'click', (e) => {
        const ext = e.target.closest('[data-shk-external-buy]');
        if (!ext) return;
        e.preventDefault();
        this.buy(true);
      });

      if (this.form) {
        this.mo = new MutationObserver(() => this.ready());
        this.mo.observe(this.form, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
      }
      this.ready();
      // Si Releasit no carga, el botón no debe quedarse bloqueado para siempre
      this.timeout = setTimeout(() => { this.fallback = true; this.ready(); }, 6000);

      on(this, 'change', (e) => { if (e.target.matches('[data-option]')) this.onOption(); });
      this.initSticky();
    }
    disconnectedCallback() {
      this.ac && this.ac.abort();
      this.mo && this.mo.disconnect();
      this.io && this.io.disconnect();
      clearTimeout(this.timeout);
    }
    ready() {
      const found = this.querySelector('#rsi_buy_now_button, .rsi_buy_now_button');
      if (found) this.native = found;
      if (this.native && !this.native.classList.contains('shk-native-cod')) this.native.classList.add('shk-native-cod');
      const available = Boolean(this.variant && this.variant.available);
      const enabled = available && ((this.native && !this.native.disabled) || this.fallback);
      this.buttons.forEach((b) => {
        if (b.disabled === Boolean(enabled)) b.disabled = !enabled;
        b.toggleAttribute('data-soldout', !available);
        const label = available ? b.dataset.label : this.dataset.unavailable;
        if (b.textContent !== label) b.textContent = label;
      });
    }
    buy(external = false) {
      const available = Boolean(this.variant && this.variant.available);
      if (available && this.native && !this.native.disabled) { this.native.click(); return; }
      if (available && this.fallback && this.form) { this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit(); return; }
      if (external && this.main) this.main.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
    }
    money(cents) {
      const cur = this.dataset.currency || 'COP';
      const txt = new Intl.NumberFormat(this.dataset.locale || 'es-CO', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(cents / 100);
      return this.dataset.showCode === 'true' ? `${txt} ${cur}` : txt;
    }
    onOption() {
      const values = [...this.querySelectorAll('[data-option]')].map((x) => x.value);
      const v = this.variants.find((x) => x.options.every((o, i) => o === values[i]));
      this.variant = v;
      if (!v) { this.ready(); return; }
      this.idInput.value = v.id;
      this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState({ variantId: v.id }, '', url);
      document.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: v } }));
      this.querySelectorAll('[data-price]').forEach((el) => { el.textContent = this.money(v.price); });
      const sale = v.compare_at_price > v.price;
      const compare = this.querySelector('[data-compare]'); const saving = this.querySelector('[data-saving]');
      if (compare) { compare.hidden = !sale; compare.textContent = sale ? this.money(v.compare_at_price) : ''; }
      if (saving) {
        saving.hidden = !sale;
        if (sale) {
          const pct = Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100);
          saving.textContent = `${this.dataset.showPct === 'true' ? `−${pct} % · ` : ''}${this.dataset.savingLabel} ${this.money(v.compare_at_price - v.price)}`;
        }
      }
      if (v.featured_media) { const g = this.querySelector('shk-gallery'); g && g.goToMedia && g.goToMedia(v.featured_media.id); }
      this.ready();
    }
    initSticky() {
      if (!this.sticky || !this.main || !('IntersectionObserver' in window)) return;
      const state = { mainVisible: true, mainAbove: false, finalVisible: false };
      const final = document.querySelector('[data-shk-final]');
      const render = () => {
        const show = !state.mainVisible && state.mainAbove && !state.finalVisible;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.toggleAttribute('inert', !show);
        this.sticky.setAttribute('aria-hidden', String(!show));
      };
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.target === this.main) {
            state.mainVisible = e.isIntersecting;
            state.mainAbove = !e.isIntersecting && e.boundingClientRect.top < 0;
          } else {
            state.finalVisible = e.isIntersecting;
          }
        });
        render();
      });
      this.io.observe(this.main);
      if (final) this.io.observe(final);
      render();
    }
  }
  if (!customElements.get('shk-buy')) customElements.define('shk-buy', ShkBuy);
})();
